const ATTEMPTS_KEY = 'recovery.refreshLoop.attempts'
const FORCED_AT_KEY = 'recovery.refreshLoop.forcedAt'
const FORCED_REASON_KEY = 'auth.forcedLogout.reason'

export type ForcedLogoutReason = 'recovery-refresh-loop'

type AttemptState = {
  attempts: number[]
}

function readState(): AttemptState {
  try {
    const raw = window.sessionStorage.getItem(ATTEMPTS_KEY)
    if (!raw) return { attempts: [] }
    const parsed = JSON.parse(raw) as Partial<AttemptState>
    const attempts = Array.isArray(parsed.attempts)
      ? parsed.attempts.filter((value): value is number => Number.isFinite(value))
      : []
    return { attempts }
  } catch {
    return { attempts: [] }
  }
}

function writeState(state: AttemptState): void {
  try {
    window.sessionStorage.setItem(ATTEMPTS_KEY, JSON.stringify(state))
  } catch {
    // ignore storage failures
  }
}

export function clearRecoveryRefreshAttempts(): void {
  try {
    window.sessionStorage.removeItem(ATTEMPTS_KEY)
  } catch {
    // ignore storage failures
  }
}

export function setForcedLogoutReason(reason: ForcedLogoutReason): void {
  try {
    window.sessionStorage.setItem(FORCED_REASON_KEY, reason)
  } catch {
    // ignore storage failures
  }
}

export function consumeForcedLogoutReason(): ForcedLogoutReason | null {
  try {
    const raw = window.sessionStorage.getItem(FORCED_REASON_KEY)
    if (!raw) return null
    window.sessionStorage.removeItem(FORCED_REASON_KEY)
    if (raw === 'recovery-refresh-loop') return raw
    return null
  } catch {
    return null
  }
}

export function wasForcedLogoutRecently(now = Date.now(), withinMs = 10_000): boolean {
  try {
    const raw = window.sessionStorage.getItem(FORCED_AT_KEY)
    const value = Number(raw ?? '0')
    if (!Number.isFinite(value) || value <= 0) return false
    return now - value < withinMs
  } catch {
    return false
  }
}

export function markForcedLogout(now = Date.now()): void {
  try {
    window.sessionStorage.setItem(FORCED_AT_KEY, String(now))
  } catch {
    // ignore storage failures
  }
}

export type RecoveryLoopDecision = {
  attemptCount: number
  shouldForceLogout: boolean
}

export function recordRecoveryRefreshAttempt(
  now = Date.now(),
  {
    windowMs = 90_000,
    threshold = 3,
  }: { windowMs?: number; threshold?: number } = {},
): RecoveryLoopDecision {
  if (typeof window === 'undefined') {
    return { attemptCount: 0, shouldForceLogout: false }
  }

  const state = readState()
  const cutoff = now - windowMs
  const attempts = state.attempts.filter((timestamp) => timestamp >= cutoff)
  attempts.push(now)
  writeState({ attempts })

  return { attemptCount: attempts.length, shouldForceLogout: attempts.length >= threshold }
}

